import { useState, useEffect, FormEvent } from 'react';
import { apiService } from '../services/api';
import { LoginResponse, MFAMethod } from '../types/api.types';
import MFAEmailConfirm from './MFAEmailConfirm';

interface MFAVerifyProps {
  sessionToken: string;
  onSuccess: (response: LoginResponse) => void;
  onMFAReset: () => void;
  onBack: () => void;
  mfaMethod?: MFAMethod | null; // issue #213: 'email' | 'email_otp' | null (legacy TOTP)
}

export default function MFAVerify({ sessionToken, onSuccess, onMFAReset, onBack, mfaMethod }: MFAVerifyProps) {
  const emailOnly = mfaMethod === 'email';
  const emailAvailable = mfaMethod === 'email' || mfaMethod === 'email_otp';

  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Email sub-mode (issue #213)
  const [emailMode, setEmailMode] = useState(false);
  const [emailData, setEmailData] = useState<{ expiresIn: number; emailMasked: string } | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const enterEmailMode = async () => {
    setError('');
    setSendingEmail(true);
    try {
      const resp = await apiService.sendMFAEmailCode(sessionToken);
      setEmailData({ expiresIn: resp.expires_in, emailMasked: resp.email_masked });
      setEmailMode(true);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setSendingEmail(false);
    }
  };

  // Email-only users go straight to the email challenge.
  useEffect(() => {
    if (emailOnly) {
      enterEmailMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError('Please enter a code');
      return;
    }
    if (!useBackupCode && trimmedCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.verifyMFA({
        session_token: sessionToken,
        code: trimmedCode,
        code_type: useBackupCode ? 'backup' : 'totp',
      });
      onSuccess(response);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Email challenge sub-view
  if (emailMode && emailData) {
    return (
      <MFAEmailConfirm
        title="Two-Factor Authentication"
        emailMasked={emailData.emailMasked}
        expiresIn={emailData.expiresIn}
        onVerify={async (c) => {
          const response = await apiService.verifyMFA({ session_token: sessionToken, code: c, code_type: 'email' });
          onSuccess(response);
        }}
        onResend={async () => {
          const resp = await apiService.sendMFAEmailCode(sessionToken);
          setEmailData({ expiresIn: resp.expires_in, emailMasked: resp.email_masked });
          return resp.expires_in;
        }}
        onBack={emailOnly ? onBack : () => { setEmailMode(false); setError(''); }}
        backLabel={emailOnly ? 'Back to login' : 'Use authenticator app instead'}
      />
    );
  }

  // Sending the first email code for an email-only user
  if (emailOnly && sendingEmail) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center text-gray-500">Sending code...</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Two-Factor Authentication</h2>
      <p className="text-gray-600 text-sm mb-6">
        {useBackupCode
          ? 'Enter one of your backup codes'
          : 'Enter the 6-digit code from your authenticator app'}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
            {useBackupCode ? 'Backup Code' : 'Authentication Code'}
          </label>
          <input
            id="code"
            type="text"
            value={code}
            onChange={(e) => {
              if (useBackupCode) {
                setCode(e.target.value);
              } else {
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              }
            }}
            className={`w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              useBackupCode ? '' : 'text-center text-2xl tracking-widest'
            }`}
            placeholder={useBackupCode ? 'Enter backup code' : '000000'}
            maxLength={useBackupCode ? undefined : 6}
            autoComplete="one-time-code"
            autoFocus
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || (!useBackupCode && code.length !== 6)}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>
      </form>

      <div className="mt-4 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setUseBackupCode(!useBackupCode);
            setCode('');
            setError('');
          }}
          className="text-sm text-blue-600 hover:underline"
        >
          {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code'}
        </button>

        {emailAvailable && (
          <button
            type="button"
            onClick={enterEmailMode}
            disabled={sendingEmail}
            className="text-sm text-blue-600 hover:underline disabled:text-gray-400"
          >
            {sendingEmail ? 'Sending code...' : 'Device not available? Email me a code'}
          </button>
        )}

        <button
          type="button"
          onClick={onMFAReset}
          className="text-sm text-gray-600 hover:underline"
        >
          Can't access your authenticator?
        </button>

        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-600 hover:underline"
        >
          Back to login
        </button>
      </div>
    </div>
  );
}
